import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Award, Users, Clock, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import QuoteForm from "@/components/QuoteForm";
import SEO from "@/components/SEO";
import { useState } from "react";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const ValueCard = ({ icon: Icon, title, description }) => (
  <motion.div variants={fadeInUp} className="bg-white p-8 text-center">
    <div className="w-16 h-16 bg-[#0F4C81]/10 rounded-full flex items-center justify-center mx-auto mb-4">
      <Icon className="w-8 h-8 text-[#0F4C81]" />
    </div>
    <h3 className="font-heading text-xl font-medium text-[#1A202C] mb-2">{title}</h3>
    <p className="font-body text-[#4B5563]">{description}</p>
  </motion.div>
);

const AboutPage = () => {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <div className="pt-20">
      <SEO
        title="About Us"
        description="Learn about Brunswick Glass - a locally owned glass company serving coastal North Carolina for over 30 years. Quality craftsmanship, reliable timelines, and licensed & insured professionals."
        keywords="about Brunswick Glass, glass company NC, coastal NC glass experts, local glass company, 30 years experience"
      />
      
      {/* Hero Section */}
      <section className="relative min-h-[40vh] flex items-center bg-[#F9F9F7]">
        <div className="section-container relative z-10 w-full py-16">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl">
            <motion.span variants={fadeInUp} className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4">About Us</motion.span>
            <motion.h1 variants={fadeInUp} className="font-heading text-5xl md:text-6xl font-medium tracking-tight leading-[1.1] text-[#1A202C] mb-6">Clear Quality.<br />Coastal Craftsmanship.</motion.h1>
            <motion.p variants={fadeInUp} className="font-body text-xl leading-relaxed text-[#4B5563]">Trusted by homeowners and businesses across coastal North Carolina for over 30 years.</motion.p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-[#0F4C81]">
        <div className="section-container">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div><div className="font-heading text-5xl font-medium text-white mb-2">30+</div><div className="font-body text-white/70">Years of Experience</div></div>
            <div><div className="font-heading text-5xl font-medium text-white mb-2">5000+</div><div className="font-body text-white/70">Projects Completed</div></div>
            <div><div className="font-heading text-5xl font-medium text-white mb-2">100%</div><div className="font-body text-white/70">Customer Satisfaction</div></div>
          </div>
        </div>
      </section>

      {/* Story Section - Split into multiple sections */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Who We Are</motion.h2>
              
              <motion.p variants={fadeInUp} className="font-body text-lg leading-relaxed text-[#4B5563] mb-4">
                Brunswick Glass is a locally owned glass company serving homeowners and small businesses across coastal North Carolina. From custom frameless showers to window glass replacement and light commercial glazing, we deliver precise measurements, clean installs, and dependable timelines—every time.
              </motion.p>
              
              <motion.p variants={fadeInUp} className="font-body text-lg leading-relaxed text-[#4B5563]">
                With decades of experience and a hands-on approach, our team helps you choose the right glass, hardware, and finishes for your space. Whether you're refreshing a bathroom, replacing broken glass, or upgrading storefront windows, we make the process straightforward and stress-free.
              </motion.p>
            </motion.div>
            
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
              <img 
                src="https://images.unsplash.com/photo-1721564130772-c9ee561ab87b?w=800&q=80" 
                alt="Brunswick Glass professional at work" 
                className="w-full h-[380px] object-cover" 
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our History Section */}
      <section className="section-spacing bg-[#F9F9F7]">
        <div className="section-container">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="order-2 lg:order-1">
              <img 
                src="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80" 
                alt="Quality glass installation" 
                className="w-full h-[380px] object-cover" 
              />
            </motion.div>
            
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="order-1 lg:order-2">
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Our History</motion.h2>
              
              <motion.p variants={fadeInUp} className="font-body text-lg leading-relaxed text-[#4B5563] mb-4">
                Brunswick Glass has proudly served the coastal North Carolina community for more than 30 years. Built on craftsmanship, reliability, and local relationships, we've earned a reputation for doing the job right the first time.
              </motion.p>
              
              <motion.p variants={fadeInUp} className="font-body text-lg leading-relaxed text-[#4B5563]">
                Today, we continue that tradition with a renewed focus on customer experience, clear communication, and modern glass solutions. Our team brings hands-on expertise to every project—from careful measurement through final installation—so customers know exactly what to expect.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Commitment Section */}
      <section className="py-12 bg-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer}>
              <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Our Commitment</motion.h2>
              
              <motion.p variants={fadeInUp} className="font-body text-lg leading-relaxed text-[#4B5563] mb-4">
                As a local company, we understand the needs of coastal homes and businesses, and we stand behind our work. Whether your project is large or small, you'll receive the same attention to detail and respect for your space.
              </motion.p>
              
              <motion.p variants={fadeInUp} className="font-heading text-2xl font-medium text-[#0F4C81]">
                Brunswick Glass—The Clear Choice for Quality Glass.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="section-spacing bg-[#F9F9F7]">
        <div className="section-container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-10">
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Why Choose Us</motion.h2>
            <motion.p variants={fadeInUp} className="font-body text-lg text-[#4B5563] max-w-2xl mx-auto">We're committed to delivering exceptional quality and service on every project.</motion.p>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid md:grid-cols-4 gap-6">
            <ValueCard icon={Award} title="Quality Craftsmanship" description="Precise measurements and professional installation on every job." />
            <ValueCard icon={Users} title="Local Expertise" description="We understand the unique needs of coastal NC homes and businesses." />
            <ValueCard icon={Clock} title="Reliable Timelines" description="We show up when we say we will and complete projects on schedule." />
            <ValueCard icon={Shield} title="Licensed & Insured" description="Full coverage and peace of mind for your property and investment." />
          </motion.div>
        </div>
      </section>

      {/* Services - Simple list layout matching homepage */}
      <section className="section-spacing bg-white">
        <div className="section-container">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center mb-8">
            <motion.h2 variants={fadeInUp} className="font-heading text-4xl font-medium text-[#1A202C] mb-4">Our Services</motion.h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center">
              <motion.h3 variants={fadeInUp} className="font-heading text-xl font-medium text-[#1A202C] mb-4">Residential</motion.h3>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3">
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Custom Shower Glass</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Window Glass Replacement</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Custom Mirrors</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Screen Repair</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Table Top Glass</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Glass Shelves</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Marine & Boat Glass</span>
              </motion.div>
            </motion.div>
            
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="text-center">
              <motion.h3 variants={fadeInUp} className="font-heading text-xl font-medium text-[#1A202C] mb-4">Commercial</motion.h3>
              <motion.div variants={fadeInUp} className="flex flex-wrap justify-center gap-3">
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Storefront Glass</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Curtain Walls</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Aluminum Doors</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Glass Handrails</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Gym Mirrors</span>
                <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">System Repairs</span>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-[#0F4C81]">
        <div className="section-container text-center">
          <h2 className="font-heading text-4xl font-medium text-white mb-4">Ready to Get Started?</h2>
          <p className="font-body text-xl text-white/80 mb-8 max-w-2xl mx-auto">Contact us today for a free consultation. We'd love to help with your next glass project.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
              <DialogTrigger asChild><Button className="bg-white text-[#0F4C81] hover:bg-white/90 rounded-full px-8 h-14 font-body font-semibold">Get a Free Quote</Button></DialogTrigger>
              <DialogContent className="sm:max-w-[500px] p-8"><DialogHeader><DialogTitle className="font-heading text-2xl">Request a Free Quote</DialogTitle></DialogHeader><QuoteForm onClose={() => setQuoteOpen(false)} /></DialogContent>
            </Dialog>
            <Link to="/contact"><Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-[#0F4C81] rounded-full px-8 h-14 font-body font-semibold">Contact Us</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
